import { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    descriptionForCapability,
    getAutomationIdByType,
    getIsAppleAppStore,
    massageMonitoringData,
    stabilityIssuesSupportedForIntegration,
} from '@/helpers';
import { RootState } from '@/reducers/reducers';
import {
    selectAppPlatform,
    selectAppStoreIntegration,
    selectAppStoreType,
    selectAutomationById,
    selectChecklistItemsForReleaseStep,
    selectChecklistItemsForRolloutCount,
    selectCompletedChecklistItemsForRolloutCount,
    selectFeatureFlaggingIntegration,
    selectIsDemoInstanceOrDemoModeEnabled,
    selectIsReleaseFromScraper,
    selectMonitoringMetricsById,
    selectObservabilityAndAnalyticsIntegration,
    selectRelease,
    selectReleaseStepHasBlockedAutomation,
    selectReleaseStepHasFailedAutomation,
    selectReleaseVersion,
    selectRolloutMonitoring,
    selectStabilityIntegration,
    selectIsWorkflowsCIStepEnabled,
    selectWorkflowEnabledSteps,
} from '@/selectors/selectors';
import {
    AppMonitoringHealthMetricCategory,
    AppPlatform,
    AppStoreAvailableBuild,
    AppStoreReviewData,
    AppStoreType,
    AutomationAutoStagedRollout,
    AutomationRun,
    AutomationType,
    FeatureFlag,
    FeatureFlagTimeSeriesDatum,
    IntegrationCapability,
    IntegrationType,
    LaunchDarklyIntegrationData,
    LifecycleStep,
    ObservabilityAndAnalyticsMetricType,
    OptimizelyIntegrationData,
    ReleaseAdoption,
    ReleaseNav,
    ReleaseStepType,
    SettingType,
    StabilityMetricType,
    SubscriptionFeatureType,
    UserRatingsMetricType,
} from '@/types';
import {
    FeatureSection,
    GroupedMonitoringStatistic,
    Nullable,
    ReleaseComponentProps,
    ReleaseRolloutRouteComponentMatchParams,
    StepRunComponentProps,
} from '@/typescriptHelpers';
import RatingsReviews from '../RatingsReviews';
import { antGrey, runwayColors, titleStyle } from '../styles/globalStyles';
import { CollapsibleSection } from './CollapsibleSection';
import { MonitoringStatCard, MonitoringStatLoadingCard } from './MonitoringStatCard';
import StabilityIssuesContainer from './StabilityIssuesContainer';
import {
    createAppSettingsRouteWithSettingType,
    createReleaseRolloutRoute,
    ReleaseStepTabType,
} from '@/helpers/routeHelpers';
import { Link, useHistory, useParams } from 'react-router-dom';
import AdoptionRateChart from './AdoptionRateChart';
import { useLifecycleStepCompleted } from '../automations/automationHelperComponents';
import {
    CheckCircleFilled,
    CloseCircleFilled,
    DashboardOutlined,
    ExclamationCircleFilled,
    ThunderboltFilled,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { FetchableEntityType } from '@/entityType';
import { globalMessage } from '@/utils/globalMessage';
import { tryFetchEntity } from '@/actions/entities';
import { trackReleaseRouteEvent } from '@/actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import {
    CapabilityIntegrationsListItem,
    ConnectIntegrationsBannerWrapper,
    DefaultDocsLinkNode,
} from '../ConnectIntegrationsBanner';
import Space from 'antd/lib/space';
import Alert from 'antd/lib/alert';
import Typography from 'antd/lib/typography';
import List from 'antd/lib/list';
import Tabs from 'antd/lib/tabs';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import { EmptyStepBanner } from '../releaseEmptyState/EmptyStateBanner';
import Layout from 'antd/lib/layout';
import useSetLastViewedReleaseStep from '../hooks/useSetLastViewedReleaseStep';
import Text from 'antd/lib/typography/Text';
import ReleaseStepChecklistContainer from '../releaseSteps/ReleaseStepChecklistContainer';
import { useError, useIsLoading } from '@/utils/hooks/helpers';
import ErrorBoundary from '@/components/ErrorBoundary';
import AutomationTab from '@/components/automations/AutomationTab';
import FeatureFlagDrawer from '@/components/featureFlags/FeatureFlagDrawer';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { RolloutMonitoringSummaryContent } from './MonitoringSummaryContent';

export enum MonitoringSectionFilter {
    All = 'all',
    Stability = 'stability',
    Analytics = 'analytics',
    Reviews = 'reviews',
}

export enum MonitoringSectionTitle {
    Stability = 'Stability',
    Analytics = 'Observability & analytics',
    Reviews = 'Reviews',
}

const MonitoringStatsCardsGrid: React.FC = ({ children }) => {
    return (
        <div
            style={{
                display: 'grid',
                width: '100%',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 20,
            }}
        >
            {children}
        </div>
    );
};

const MonitoringSection: React.FC<{ title: string; shouldApplyRestrictedTooltipForRollouts: boolean }> = ({
    title,
    shouldApplyRestrictedTooltipForRollouts,
    children,
}) => {
    const marginTop = title ? 34 : 0;
    const titleComponent = (
        <Typography.Text
            style={{
                fontSize: 20,
                color: antGrey.grey7,
            }}
        >
            {title}
        </Typography.Text>
    );
    return (
        <>
            <Content style={{ width: '100%' }}>
                <Space direction="vertical" style={{ width: '100%', marginTop: marginTop }} size={20}>
                    {shouldApplyRestrictedTooltipForRollouts ?
                        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts} requiresTooltip>
                            {titleComponent}
                        </PlanRestrictedFeatureWrapper>
                    :   title && titleComponent}
                    {children}
                </Space>
            </Content>
        </>
    );
};

export const AdoptionChartWrapper: React.FC<{
    selectedBuildSessionAdoption?: number;
    releaseAdoptions: ReleaseAdoption[];
    isMultiApk: boolean;
    stabilityIntegrationType?: IntegrationType;
    currentReleaseVersion: string;
    shouldShowDisabledUI?: boolean;
}> = (props) => {
    const {
        releaseAdoptions,
        isMultiApk,
        currentReleaseVersion,
        stabilityIntegrationType,
        selectedBuildSessionAdoption,
        shouldShowDisabledUI,
    } = props;

    return (
        releaseAdoptions && (
            <AdoptionRateChart
                sessionAdoption={selectedBuildSessionAdoption}
                title={'Adoption rate (sessions)'}
                adoptionRates={releaseAdoptions}
                isMultiAPK={isMultiApk}
                currentReleaseVersion={currentReleaseVersion}
                stabIntegrationId={stabilityIntegrationType}
                shouldShowDisabledUI={shouldShowDisabledUI}
            />
        )
    );
};

export type CalculatedDataPerSection = Partial<
    Record<
        AppMonitoringHealthMetricCategory,
        {
            groupedStatistics: GroupedMonitoringStatistic[];
            shouldShowAlert: boolean;
        }
    >
>;

export type SectionsProps = ReleaseComponentProps & {
    selectedBuild: AppStoreAvailableBuild;
    isLoading: boolean;
    section: MonitoringSectionFilter;
    isPhased: boolean;
    calculatedDataPerSection: CalculatedDataPerSection;
    monitoringSettingsLink: string;
    releasedAt: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    appStoreSelectedBuilds: AppStoreAvailableBuild[];
    stabilityIntegrationType?: IntegrationType;
    isReleaseFromScraper: boolean;
    shouldShowDisabledUI?: boolean;
    appStoreReviewData: Nullable<AppStoreReviewData>;
    rolloutStartedAt: Nullable<string>;
    featureFlags: FeatureFlagTimeSeriesDatum[];
    autoHaltUnstablePhasedReleasesAutomationId: Nullable<string>;
    autoPromoteStablePhasedReleasesAutomationId: Nullable<string>;
    autoStagedRolloutAutomationId: Nullable<string>;
    automationRuns: AutomationRun[];
    onShowFeatureFlag?: (flag: FeatureFlag) => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    isAutomatedPhasedRollout: boolean;
};

export const StabilitySection: React.FC<SectionsProps> = ({
    selectedBuild,
    appId,
    orgId,
    calculatedDataPerSection,
    isLoading,
    monitoringSettingsLink,
    releasedAt,
    releaseId,
    platform,
    appStoreType,
    isReleaseFromScraper,
    shouldShowDisabledUI,
    featureFlags,
    onShowFeatureFlag,
}) => {
    let stabilityStats: GroupedMonitoringStatistic[] = [];

    const sectionData = calculatedDataPerSection.stability;

    if (sectionData) {
        stabilityStats = sectionData.groupedStatistics;
    }

    const stabilityIntegration = useSelector((state: RootState) => selectStabilityIntegration(state, appId));
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));

    const showStabilityIssues =
        selectedBuild &&
        selectedBuild.stabilityInfo &&
        stabilityIssuesSupportedForIntegration(selectedBuild.stabilityInfo.integrationId) &&
        !workflowEnabledSteps[ReleaseStepType.Rollout]; // TODO-WFs: un-hide when we return stability issues on rollout steprunapi

    return (
        <MonitoringSection title="Stability" shouldApplyRestrictedTooltipForRollouts={true}>
            {shouldShowDisabledUI ?
                <div
                    style={{
                        backgroundColor: antGrey.grey2,
                        padding: 20,
                        marginBottom: 20,
                        borderRadius: 8,
                        width: '100%',
                    }}
                >
                    <Typography.Text
                        style={{
                            color: antGrey.grey7,
                            fontSize: 12,
                        }}
                    >
                        Stability metrics not available on Free plan.
                    </Typography.Text>
                </div>
            :   <>
                    {!isReleaseFromScraper && !stabilityIntegration ?
                        <ConnectIntegrationsBannerWrapper>
                            <List
                                itemLayout="vertical"
                                dataSource={[IntegrationCapability.StabilityMonitoring]}
                                renderItem={(item) => {
                                    const description = descriptionForCapability(
                                        IntegrationCapability.StabilityMonitoring,
                                    );
                                    const docsNodeForCapability = (
                                        <DefaultDocsLinkNode capability={IntegrationCapability.StabilityMonitoring} />
                                    );
                                    return (
                                        <CapabilityIntegrationsListItem
                                            appId={appId}
                                            orgId={orgId}
                                            capability={item}
                                            description={description}
                                            docsNodeForCapability={docsNodeForCapability}
                                        />
                                    );
                                }}
                            />
                        </ConnectIntegrationsBannerWrapper>
                    :   <>
                            {sectionData?.shouldShowAlert && (
                                <Alert
                                    message={
                                        <Typography.Text>
                                            One or more of your stability events is missing product health threshold
                                            definitions. Visit{' '}
                                            <Link to={monitoringSettingsLink}>App Settings {'>'} Monitoring</Link> to
                                            add rules.
                                        </Typography.Text>
                                    }
                                    type="warning"
                                    showIcon
                                />
                            )}
                            {isReleaseFromScraper || isLoading ?
                                <EmptyStateWrapper
                                    appId={appId}
                                    featureSections={[FeatureSection.RolloutStabilityModule]}
                                    releaseId={releaseId}
                                    shouldCenterContent
                                >
                                    <div>
                                        <MonitoringStatsCardsGrid>
                                            {Array(2)
                                                .fill(0)
                                                .map((_, index) => (
                                                    <MonitoringStatLoadingCard
                                                        key={`${index}`}
                                                        disableLoadShimmer={isReleaseFromScraper}
                                                    />
                                                ))}
                                        </MonitoringStatsCardsGrid>
                                    </div>
                                </EmptyStateWrapper>
                            :   <MonitoringStatsCardsGrid>
                                    {stabilityStats
                                        .filter((groupedMonitoringStatistic) => {
                                            if (groupedMonitoringStatistic.groups.length > 0) {
                                                const hiddenStatsCardTypes = [
                                                    StabilityMetricType.UserStabilityDeltaFromPreviousPercentage,
                                                    StabilityMetricType.UserStabilityDeltaFromPreviousValue,
                                                    StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage,
                                                    StabilityMetricType.SessionStabilityDeltaFromPreviousValue,
                                                ];
                                                return !hiddenStatsCardTypes.includes(
                                                    groupedMonitoringStatistic.groups[0].type as StabilityMetricType,
                                                );
                                            } else {
                                                return true;
                                            }
                                        })
                                        .map((groupedMonitoringStatistic) => {
                                            return (
                                                <MonitoringStatCard
                                                    {...groupedMonitoringStatistic}
                                                    releasedAt={releasedAt}
                                                    platform={platform}
                                                    appStoreType={appStoreType}
                                                    key={groupedMonitoringStatistic.key}
                                                    shouldShowAlert={sectionData?.shouldShowAlert}
                                                    featureFlags={featureFlags}
                                                    onShowFeatureFlag={onShowFeatureFlag}
                                                />
                                            );
                                        })}
                                </MonitoringStatsCardsGrid>
                            }
                            {showStabilityIssues && <StabilityIssuesContainer appId={appId} build={selectedBuild} />}
                        </>
                    }
                </>
            }
        </MonitoringSection>
    );
};

export const AnalyticsSection: React.FC<SectionsProps> = ({
    isLoading,
    calculatedDataPerSection,
    monitoringSettingsLink,
    appId,
    orgId,
    releaseId,
    releasedAt,
    platform,
    appStoreType,
    shouldShowDisabledUI,
    isReleaseFromScraper,
    featureFlags,
    onShowFeatureFlag,
}) => {
    const sectionData = calculatedDataPerSection.productAnalytics;

    const analyticsIntegration = useSelector((state: RootState) =>
        selectObservabilityAndAnalyticsIntegration(state, appId),
    );

    return (
        <MonitoringSection title="Observability & analytics" shouldApplyRestrictedTooltipForRollouts={true}>
            {shouldShowDisabledUI ?
                <div
                    style={{
                        backgroundColor: antGrey.grey2,
                        padding: 20,
                        marginBottom: 20,
                        borderRadius: 8,
                        width: '100%',
                    }}
                >
                    <Typography.Text
                        style={{
                            color: antGrey.grey7,
                            fontSize: 12,
                        }}
                    >
                        Observability + analytics metrics not available on Free plan.
                    </Typography.Text>
                </div>
            :   <>
                    {!isReleaseFromScraper && !analyticsIntegration ?
                        <ConnectIntegrationsBannerWrapper>
                            <List
                                itemLayout="vertical"
                                dataSource={[IntegrationCapability.ObservabilityAndAnalytics]}
                                renderItem={(item) => {
                                    const description = descriptionForCapability(
                                        IntegrationCapability.ObservabilityAndAnalytics,
                                    );
                                    const docsNodeForCapability = (
                                        <DefaultDocsLinkNode
                                            capability={IntegrationCapability.ObservabilityAndAnalytics}
                                        />
                                    );
                                    return (
                                        <CapabilityIntegrationsListItem
                                            appId={appId}
                                            orgId={orgId}
                                            capability={item}
                                            description={description}
                                            docsNodeForCapability={docsNodeForCapability}
                                        />
                                    );
                                }}
                            />
                        </ConnectIntegrationsBannerWrapper>
                    :   <>
                            {sectionData?.shouldShowAlert && (
                                <Alert
                                    message={
                                        <Typography.Text>
                                            One or more of your analytics events is missing product health threshold
                                            definitions. Visit{' '}
                                            <Link to={monitoringSettingsLink}>App Settings {'>'} Monitoring</Link> to
                                            add rules.
                                        </Typography.Text>
                                    }
                                    type="warning"
                                    showIcon
                                />
                            )}
                            {isReleaseFromScraper || isLoading ?
                                <EmptyStateWrapper
                                    appId={appId}
                                    featureSections={[FeatureSection.RolloutObservabilityAndAnalyticsModule]}
                                    releaseId={releaseId}
                                    shouldCenterContent
                                >
                                    <div>
                                        <MonitoringStatsCardsGrid>
                                            {Array(2)
                                                .fill(0)
                                                .map((_, index) => (
                                                    <MonitoringStatLoadingCard
                                                        key={`${index}`}
                                                        disableLoadShimmer={isReleaseFromScraper}
                                                    />
                                                ))}
                                        </MonitoringStatsCardsGrid>
                                    </div>
                                </EmptyStateWrapper>
                            :   <MonitoringStatsCardsGrid>
                                    {sectionData?.groupedStatistics
                                        .filter((groupedMonitoringStatistic) => {
                                            if (groupedMonitoringStatistic.groups.length > 0) {
                                                const hiddenStatsCardTypes = [
                                                    ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage,
                                                    ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue,
                                                    ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage,
                                                    ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue,
                                                    ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage,
                                                    ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue,
                                                    ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage,
                                                    ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue,
                                                    ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage,
                                                    ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue,
                                                    ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage,
                                                    ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue,
                                                    ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage,
                                                    ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue,
                                                ];
                                                return !hiddenStatsCardTypes.includes(
                                                    groupedMonitoringStatistic.groups[0]
                                                        .type as ObservabilityAndAnalyticsMetricType,
                                                );
                                            } else {
                                                return true;
                                            }
                                        })
                                        .map((groupedMonitoringStatistic) => {
                                            return (
                                                <MonitoringStatCard
                                                    {...groupedMonitoringStatistic}
                                                    platform={platform}
                                                    appStoreType={appStoreType}
                                                    releasedAt={releasedAt}
                                                    key={groupedMonitoringStatistic.key}
                                                    shouldShowAlert={sectionData?.shouldShowAlert}
                                                    featureFlags={featureFlags}
                                                    onShowFeatureFlag={onShowFeatureFlag}
                                                />
                                            );
                                        })}
                                </MonitoringStatsCardsGrid>
                            }
                        </>
                    }
                </>
            }
        </MonitoringSection>
    );
};

export const ReviewsSection: React.FC<SectionsProps> = ({
    releaseId,
    appId,
    orgId,
    calculatedDataPerSection,
    isLoading,
    releasedAt,
    monitoringSettingsLink,
    platform,
    appStoreType,
    shouldShowDisabledUI,
    isReleaseFromScraper,
    appStoreReviewData,
    featureFlags,
    onShowFeatureFlag,
}) => {
    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));

    const sectionData = calculatedDataPerSection.userReviews;

    return (
        <MonitoringSection title="Reviews" shouldApplyRestrictedTooltipForRollouts={false}>
            {!isReleaseFromScraper && !appStoreIntegration && (
                <ConnectIntegrationsBannerWrapper>
                    <List
                        itemLayout="vertical"
                        dataSource={[IntegrationCapability.AppStore]}
                        renderItem={(item) => {
                            const description = descriptionForCapability(IntegrationCapability.AppStore);
                            const docsNodeForCapability = (
                                <DefaultDocsLinkNode capability={IntegrationCapability.AppStore} />
                            );
                            return (
                                <CapabilityIntegrationsListItem
                                    appId={appId}
                                    orgId={orgId}
                                    capability={item}
                                    description={description}
                                    docsNodeForCapability={docsNodeForCapability}
                                />
                            );
                        }}
                    />
                </ConnectIntegrationsBannerWrapper>
            )}
            {sectionData?.shouldShowAlert && (
                <Alert
                    message={
                        <Typography.Text>
                            Your ratings is missing product health threshold definitions. Visit{' '}
                            <Link to={monitoringSettingsLink}>App Settings {'>'} Monitoring</Link> to add rules.
                        </Typography.Text>
                    }
                    type="warning"
                    showIcon
                />
            )}
            <EmptyStateWrapper
                appId={appId}
                featureSections={[FeatureSection.RolloutUserReviewsModule]}
                releaseId={releaseId}
                shouldCenterContent
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 20,
                    }}
                >
                    {isReleaseFromScraper || isLoading ?
                        <MonitoringStatsCardsGrid>
                            {Array(2)
                                .fill(0)
                                .map((_, index) => (
                                    <MonitoringStatLoadingCard
                                        key={`${index}`}
                                        disableLoadShimmer={isReleaseFromScraper}
                                    />
                                ))}
                        </MonitoringStatsCardsGrid>
                    :   <MonitoringStatsCardsGrid>
                            {sectionData?.groupedStatistics
                                .filter((groupedMonitoringStatistic) => {
                                    const hiddenStatsCardTypes = [
                                        UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage,
                                        UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue,
                                    ];
                                    return (
                                        groupedMonitoringStatistic.groups.length == 0 ||
                                        !hiddenStatsCardTypes.includes(
                                            groupedMonitoringStatistic.groups[0].type as UserRatingsMetricType,
                                        )
                                    );
                                })
                                .map((groupedMonitoringStatistic) => {
                                    const userRatingMonitoringStatisticGroup = groupedMonitoringStatistic.groups.find(
                                        (group) => {
                                            return group.type === UserRatingsMetricType.AvgUserRating;
                                        },
                                    );
                                    return (
                                        <MonitoringStatCard
                                            {...groupedMonitoringStatistic}
                                            releasedAt={releasedAt}
                                            platform={platform}
                                            appStoreType={appStoreType}
                                            key={groupedMonitoringStatistic.key}
                                            shouldShowAlert={sectionData.shouldShowAlert}
                                            shouldApplyRestrictedTooltipForRollouts={
                                                !!userRatingMonitoringStatisticGroup
                                            }
                                            shouldShowDisabledUI={shouldShowDisabledUI}
                                            featureFlags={featureFlags}
                                            onShowFeatureFlag={onShowFeatureFlag}
                                        />
                                    );
                                })}
                        </MonitoringStatsCardsGrid>
                    }
                    {appStoreReviewData && (
                        <CollapsibleSection title="Reviews" integrationType={appStoreIntegration?.id}>
                            <RatingsReviews reviewData={appStoreReviewData} orgId={orgId} appId={appId} />
                        </CollapsibleSection>
                    )}
                </div>
            </EmptyStateWrapper>
        </MonitoringSection>
    );
};

export const MonitoringContainer: React.FC<ReleaseComponentProps> = (props: ReleaseComponentProps) => {
    const { releaseId, appId, orgId } = props;
    const dispatch = useDispatch();

    const [selectedFeatureFlag, setSelectedFeatureFlag] = React.useState<FeatureFlag>(null);

    const checklistItems = useSelector((state: RootState) =>
        selectChecklistItemsForReleaseStep(state, props.releaseId, ReleaseStepType.Rollout),
    );

    const [selectedSection, setSelectedSection] = React.useState<MonitoringSectionFilter>(MonitoringSectionFilter.All);

    const releaseReleasedAt = useSelector((state: RootState) => selectRelease(state, releaseId).releasedAt);
    const rolloutMonitoring = useSelector((state: RootState) => selectRolloutMonitoring(state, props.releaseId));
    const phasedRelease = useSelector((state: RootState) => selectRelease(state, releaseId).phasedRelease);

    const featureFlaggingIntegration = useSelector((state: RootState) =>
        selectFeatureFlaggingIntegration(state, props.appId),
    );
    const [featureFlaggingIntegrationIcon, featureFlaggingIntegrationIconAlt] =
        featureFlaggingIntegration ? iconAndAltForIntegrationType(featureFlaggingIntegration.id) : [null, null];

    const appStoreAvailableBuilds = rolloutMonitoring?.appStoreSelectedBuilds ?? [];
    const appStoreReviewData = rolloutMonitoring?.appStoreReviewData;
    const rolloutStartedAt = rolloutMonitoring?.rolloutStartedAt;

    const autoHaltUnstablePhasedReleasesAutomationId = getAutomationIdByType(
        rolloutMonitoring?.automationRuns ?? [],
        AutomationType.AutoHaltUnstablePhasedReleases,
    );
    const autoPromoteStablePhasedReleasesAutomationId = getAutomationIdByType(
        rolloutMonitoring?.automationRuns ?? [],
        AutomationType.AutoPromoteStablePhasedReleases,
    );

    const autoStagedRolloutAutomationId = getAutomationIdByType(
        rolloutMonitoring?.automationRuns ?? [],
        AutomationType.AutoStagedRollout,
    );

    const [selectedBuild, setSelectedBuild] = React.useState(null);

    React.useEffect(() => {
        if (appStoreAvailableBuilds.length > 0 && !selectedBuild) {
            setSelectedBuild(appStoreAvailableBuilds[0]);
        }
    }, [appStoreAvailableBuilds]);

    const isLoadingRolloutMonitoring = useIsLoading(FetchableEntityType.RolloutMonitoring, releaseId);
    const fetchRolloutMonitoringError = useError(FetchableEntityType.RolloutMonitoring, releaseId);

    const metricsById = useSelector((state: RootState) => selectMonitoringMetricsById(state, props.appId));

    let groupedMonitoringStatisticsForCards = massageMonitoringData({
        monitoringStatistics: rolloutMonitoring?.monitoringStatistics ?? [],
        monitoringAutomations: rolloutMonitoring?.monitoringAutomations ?? [],
        metricsById,
    });

    let groupedMonitoringStatistics = massageMonitoringData({
        monitoringStatistics: rolloutMonitoring?.monitoringStatistics ?? [],
        monitoringAutomations: rolloutMonitoring?.monitoringAutomations ?? [],
        metricsById,
        shouldGroupCrashFreeStats: false,
    });

    const isMultiApk = appStoreAvailableBuilds?.length > 1;
    if (isMultiApk) {
        groupedMonitoringStatisticsForCards = groupedMonitoringStatisticsForCards.filter((stat) => {
            if (stat.category !== AppMonitoringHealthMetricCategory.Stability) {
                return true;
            }

            return stat.buildIdentifier === selectedBuild?.appStoreBuild.buildNumber;
        });

        groupedMonitoringStatistics = groupedMonitoringStatistics.filter((stat) => {
            if (stat.category !== AppMonitoringHealthMetricCategory.Stability) {
                return true;
            }

            return stat.buildIdentifier === selectedBuild?.appStoreBuild.buildNumber;
        });
    }

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const isReleaseFromScraper = useSelector((state: RootState) => selectIsReleaseFromScraper(state, releaseId));
    const isDemoMode = useSelector((state: RootState) =>
        selectIsDemoInstanceOrDemoModeEnabled(state, orgId, releaseId),
    );

    const isPhased = !!phasedRelease;

    const stabilityIntegration = useSelector((state: RootState) => selectStabilityIntegration(state, appId));
    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));
    const releaseVersion = useSelector((state: RootState) => selectReleaseVersion(state, releaseId));
    const checklistItemsCount = useSelector((state: RootState) =>
        selectChecklistItemsForRolloutCount(state, releaseId),
    );
    const completedChecklistItemsCount = useSelector((state: RootState) =>
        selectCompletedChecklistItemsForRolloutCount(state, releaseId),
    );

    const isReleased = useLifecycleStepCompleted(LifecycleStep.Release, releaseId);
    const displayWaitingForRelease = !isReleased;
    const shouldFetchRolloutMonitoring = !isDemoMode;

    const allMonitoringMetricsInSettings = Object.values(metricsById);
    const calculatedDataPerSection: CalculatedDataPerSection = groupedMonitoringStatistics.reduce(
        (acc, groupedMonitoringStatistic) => {
            const data = acc[groupedMonitoringStatistic.category] ?? {
                shouldShowAlert: false,
                groupedStatistics: [],
            };

            data.groupedStatistics.push(groupedMonitoringStatistic);
            acc[groupedMonitoringStatistic.category] = data;
            return acc;
        },
        {},
    );

    Object.entries(calculatedDataPerSection).forEach(([category]) => {
        calculatedDataPerSection[category].shouldShowAlert = allMonitoringMetricsInSettings.some((metric) => {
            metric.category === category;
        });
    });

    const monitoringSettingsLink = createAppSettingsRouteWithSettingType(orgId, appId, SettingType.HealthMetrics);

    useSetLastViewedReleaseStep(releaseId, ReleaseNav.Rollouts);

    const tabsPaneStyle: React.CSSProperties = { marginTop: 15, height: '100%' };
    const params = useParams<ReleaseRolloutRouteComponentMatchParams>();
    const activeTab = params.tabTypeParam ?? ReleaseStepTabType.Summary;
    const history = useHistory();
    const completedItems = `${completedChecklistItemsCount} of ${checklistItemsCount}`;
    const hasChecklistItems = checklistItemsCount > 0;
    const allCompleted = completedChecklistItemsCount === checklistItemsCount && checklistItemsCount != 0;
    const showLoading = isLoadingRolloutMonitoring && !rolloutMonitoring;

    const hasFailedEnabledAutomation = useSelector((state: RootState) =>
        selectReleaseStepHasFailedAutomation(state, props.appId, releaseId, ReleaseStepType.Rollout),
    );
    const hasBlockedEnabledAutomation = useSelector((state: RootState) =>
        selectReleaseStepHasBlockedAutomation(state, props.appId, releaseId, ReleaseStepType.Rollout),
    );

    const automationsTabAlert =
        hasFailedEnabledAutomation ?
            <CloseCircleFilled
                style={{
                    color: runwayColors.statusRed,
                    fontSize: 16,
                    marginLeft: 8,
                    marginRight: 0,
                }}
            />
        : hasBlockedEnabledAutomation ?
            <ExclamationCircleFilled
                style={{
                    color: runwayColors.statusRed,
                    fontSize: 16,
                    marginLeft: 8,
                    marginRight: 0,
                }}
            />
        :   null;

    const handleMonitoringSectionFilterChange = (value: string) => {
        setSelectedSection(value as MonitoringSectionFilter);
    };

    const onShowFeatureFlag = (flag: FeatureFlag) => {
        setSelectedFeatureFlag(flag);
    };

    React.useEffect(() => {
        if (shouldFetchRolloutMonitoring) {
            dispatch(tryFetchEntity(FetchableEntityType.RolloutMonitoring, { orgId, appId, releaseId }, releaseId));
        }
    }, [appId, releaseId, shouldFetchRolloutMonitoring]);

    React.useEffect(() => {
        if (fetchRolloutMonitoringError) {
            globalMessage.error(fetchRolloutMonitoringError);
        }
    }, [fetchRolloutMonitoringError]);

    React.useEffect(() => {
        dispatch(
            trackReleaseRouteEvent({
                orgId: orgId,
                appId: appId,
                releaseId: releaseId,
                eventName: AnalyticEvents.Event.ViewRolloutMonitoring,
            }),
        );
    }, []);

    const featureFlags = rolloutMonitoring?.featureFlagTimeSeries ?? [];

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const automatedStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;

    const hasPhasedReleaseState = !!phasedRelease;

    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));

    // for iOS, just whether a phased release is present. For android, whether they've enabled automated rollouts
    const isAutomatedPhasedRollout = isAppleAppStore ? hasPhasedReleaseState : automatedStagedRolloutEnabled;

    return !isLoadingRolloutMonitoring && !rolloutMonitoring && !displayWaitingForRelease ?
            null
        :   <>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
                    {(isDisabled) => (
                        <Layout style={{ backgroundColor: 'white', position: 'relative' }}>
                            <EmptyStepBanner
                                appId={props.appId}
                                releaseId={props.releaseId}
                                releaseStepType={null}
                                customMessage={`The Rollout page is where you go to monitor your release as it's rolled out to users. From here, you can keep tabs on the overall health of your release by monitoring it's stability, the performance of key product analytics events, and user reviews.`}
                                isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                            />

                            <div
                                style={{
                                    maxWidth: '1400px',
                                    width: '100%',
                                    height: '100%',
                                    padding: '24px 36px 95px 36px',
                                    overflow: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Tabs
                                    className="release-step-tabs"
                                    activeKey={activeTab}
                                    type="card"
                                    onTabClick={(key) => {
                                        const tabType = key as ReleaseStepTabType;
                                        const route = createReleaseRolloutRoute(
                                            params.orgIdParam,
                                            params.appIdParam,
                                            params.releaseIdParam,
                                            tabType,
                                        );

                                        history.push(route);
                                    }}
                                    tabBarExtraContent={{
                                        left: (
                                            <div style={{ paddingRight: 24 }}>
                                                <PlanRestrictedFeatureWrapper
                                                    requiresTooltip
                                                    feature={null}
                                                    integrationCapability={null}
                                                >
                                                    <Text style={titleStyle}>{'Rollout'}</Text>
                                                </PlanRestrictedFeatureWrapper>
                                            </div>
                                        ),
                                    }}
                                >
                                    <Tabs.TabPane
                                        tab={
                                            <div>
                                                <DashboardOutlined
                                                    style={{
                                                        color:
                                                            activeTab === ReleaseStepTabType.Summary ?
                                                                runwayColors.info
                                                            :   antGrey.grey6,
                                                    }}
                                                />
                                                <Text
                                                    style={{
                                                        color:
                                                            activeTab === ReleaseStepTabType.Summary ?
                                                                antGrey.grey8
                                                            :   antGrey.grey7,
                                                    }}
                                                >
                                                    Summary
                                                </Text>
                                            </div>
                                        }
                                        style={tabsPaneStyle}
                                        key={ReleaseStepTabType.Summary}
                                    >
                                        <RolloutMonitoringSummaryContent
                                            orgId={orgId}
                                            appId={appId}
                                            releaseId={releaseId}
                                            isReleaseFromScraper={isReleaseFromScraper}
                                            displayWaitingForRelease={displayWaitingForRelease}
                                            calculatedDataPerSection={calculatedDataPerSection}
                                            releaseVersion={releaseVersion}
                                            onShowFeatureFlag={onShowFeatureFlag}
                                            setSelectedBuild={setSelectedBuild}
                                            handleMonitoringSectionFilterChange={handleMonitoringSectionFilterChange}
                                            platform={platform}
                                            isDemoMode={isDemoMode}
                                            isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                                            phasedRelease={phasedRelease}
                                            appStoreType={appStoreType}
                                            appStoreIntegrationId={appStoreIntegration?.id}
                                            rolloutMonitoring={rolloutMonitoring}
                                            groupedMonitoringStatisticsForCards={groupedMonitoringStatisticsForCards}
                                            rolloutStartedAt={rolloutStartedAt}
                                            isPhased={isPhased}
                                            isMultiApk={isMultiApk}
                                            autoHaltUnstablePhasedReleasesAutomationId={
                                                autoHaltUnstablePhasedReleasesAutomationId
                                            }
                                            autoPromoteStablePhasedReleasesAutomationId={
                                                autoPromoteStablePhasedReleasesAutomationId
                                            }
                                            autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                                            selectedBuild={selectedBuild}
                                            isLoading={showLoading}
                                            section={selectedSection}
                                            monitoringSettingsLink={monitoringSettingsLink}
                                            releasedAt={releaseReleasedAt}
                                            appStoreSelectedBuilds={appStoreAvailableBuilds}
                                            stabilityIntegrationType={stabilityIntegration?.id}
                                            shouldShowDisabledUI={null}
                                            appStoreReviewData={appStoreReviewData}
                                            featureFlags={featureFlags}
                                            automationRuns={null}
                                            stepRunComponentProps={null}
                                        />
                                    </Tabs.TabPane>
                                    <Tabs.TabPane
                                        disabled={isDisabled}
                                        className="step-tab-automations"
                                        key={ReleaseStepTabType.Automations}
                                        tab={
                                            <EmptyStateWrapper
                                                appId={appId}
                                                featureSections={[FeatureSection.AutomationTab]}
                                                shouldForceDisable={activeTab === ReleaseStepTabType.Automations}
                                                releaseId={releaseId}
                                                shouldShowAsTooltip
                                            >
                                                <div>
                                                    <ThunderboltFilled
                                                        style={{
                                                            color:
                                                                activeTab === ReleaseStepTabType.Automations ?
                                                                    runwayColors.info
                                                                :   antGrey.grey6,
                                                        }}
                                                    />
                                                    <Text
                                                        style={{
                                                            color:
                                                                activeTab === ReleaseStepTabType.Automations ?
                                                                    antGrey.grey8
                                                                :   antGrey.grey7,
                                                        }}
                                                    >
                                                        Automation
                                                    </Text>
                                                    {automationsTabAlert}
                                                </div>
                                            </EmptyStateWrapper>
                                        }
                                        style={tabsPaneStyle}
                                    >
                                        <ErrorBoundary showRedirectLink={false} key={props.releaseId}>
                                            <AutomationTab
                                                {...props}
                                                isReleaseStep={false}
                                                releaseStepType={ReleaseStepType.Rollout}
                                                automationRuns={rolloutMonitoring?.automationRuns ?? []}
                                            />
                                        </ErrorBoundary>
                                    </Tabs.TabPane>
                                    <Tabs.TabPane
                                        tab={
                                            <EmptyStateWrapper
                                                appId={props.appId}
                                                featureSections={[FeatureSection.ChecklistTab]}
                                                shouldForceDisable={activeTab === ReleaseStepTabType.Checklist}
                                                releaseId={props.releaseId}
                                                shouldShowAsTooltip
                                            >
                                                <div>
                                                    <UnorderedListOutlined
                                                        style={{
                                                            color:
                                                                activeTab === ReleaseStepTabType.Checklist ?
                                                                    runwayColors.info
                                                                :   antGrey.grey6,
                                                        }}
                                                    />
                                                    <Text
                                                        style={{
                                                            color:
                                                                activeTab === ReleaseStepTabType.Checklist ?
                                                                    antGrey.grey8
                                                                :   antGrey.grey7,
                                                        }}
                                                    >
                                                        Checklist {hasChecklistItems && '/ '}
                                                    </Text>
                                                    {hasChecklistItems && (
                                                        <Text
                                                            style={{
                                                                color:
                                                                    allCompleted ?
                                                                        runwayColors.statusGreen
                                                                    :   runwayColors.statusYellow,
                                                            }}
                                                        >
                                                            {completedItems}
                                                        </Text>
                                                    )}
                                                    {allCompleted && (
                                                        <CheckCircleFilled
                                                            style={{ color: runwayColors.statusGreen, marginLeft: 4 }}
                                                        />
                                                    )}
                                                </div>
                                            </EmptyStateWrapper>
                                        }
                                        className="step-tab-checklist"
                                        style={tabsPaneStyle}
                                        key={ReleaseStepTabType.Checklist}
                                    >
                                        <ReleaseStepChecklistContainer
                                            stepRunComponentProps={null}
                                            releaseId={releaseId}
                                            releaseStepType={ReleaseStepType.Rollout}
                                            appId={appId}
                                            orgId={orgId}
                                            checklistItems={checklistItems}
                                        />
                                    </Tabs.TabPane>
                                </Tabs>
                            </div>
                        </Layout>
                    )}
                </PlanRestrictedFeatureWrapper>
                {featureFlaggingIntegration && (
                    <FeatureFlagDrawer
                        orgId={props.orgId}
                        appId={props.appId}
                        releaseId={props.releaseId}
                        currentVersion={releaseVersion}
                        featureFlag={selectedFeatureFlag}
                        open={!!selectedFeatureFlag}
                        onClose={() => setSelectedFeatureFlag(null)}
                        integrationId={featureFlaggingIntegration.id}
                        projectName={
                            (featureFlaggingIntegration.data as OptimizelyIntegrationData | LaunchDarklyIntegrationData)
                                .selectedProject?.name
                        }
                        environmentName={
                            (featureFlaggingIntegration.data as OptimizelyIntegrationData | LaunchDarklyIntegrationData)
                                .selectedEnvironment?.name
                        }
                        integrationIcon={featureFlaggingIntegrationIcon}
                        integrationIconAlt={featureFlaggingIntegrationIconAlt}
                        integrationName={displayNameForIntegrationType(featureFlaggingIntegration.id)}
                    />
                )}
            </>;
};
